import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, Injector, OnInit, ViewChild } from '@angular/core';
import {
  FormsModule,
  ReactiveFormsModule,
  UntypedFormArray,
  UntypedFormBuilder,
  UntypedFormControl,
  UntypedFormGroup,
  Validators,
} from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { TranslateModule } from '@ngx-translate/core';
import { DataType, RuleInterface } from '@vendasta/automata';
import { GalaxyAlertModule } from '@vendasta/galaxy/alert';
import { GalaxyFormFieldModule } from '@vendasta/galaxy/form-field';
import { map, take } from 'rxjs/operators';
import { AutomationViewService } from '../../../../../automation/automation-view.service';
import { AutomationReplacementChipsComponent } from '../../../../../common/components/automation-replacement-chips/automation-replacement-chips.component';
import { SalespersonSelectorService } from '../../../../../data-services/salesperson-selector-service';
import { ADDITIONAL_SALESPEOPLE, ASSIGNED_SALESPERSON, UsersService } from '../../../../../data-services/users.service';
import { CommonActionPanelComponent } from '../../../common/common-action-panel-component.directive';
import { SendNotificationFormComponent } from '../../send-notification-common';
import { GenericNotificationID } from '../../send-notification-to-the-user/constants';

@Component({
  selector: 'automata-send-notification-salesperson-panel',
  templateUrl: './send-notification-salesperson-panel.component.html',
  styleUrls: ['./send-notification-salesperson-panel.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    CommonModule,
    TranslateModule,
    GalaxyAlertModule,
    MatButtonModule,
    MatCardModule,
    FormsModule,
    GalaxyFormFieldModule,
    ReactiveFormsModule,
    MatInputModule,
    MatIconModule,
    MatSelectModule,
    AutomationReplacementChipsComponent,
    SendNotificationFormComponent,
  ],
})
export class SendNotificationSalespersonPanelComponent extends CommonActionPanelComponent implements OnInit {
  supportedDataTypes: DataType[] = [DataType.DATA_TYPE_SMB_USER_ID, DataType.DATA_TYPE_STRING];

  sscLinkTemplate: string;
  form: UntypedFormGroup;
  usersFormControl: UntypedFormControl = new UntypedFormControl([]);
  jobTitleFormControl: UntypedFormControl = new UntypedFormControl({ disabled: true });

  salespersonSelectorService = this.injector.get(SalespersonSelectorService);

  @ViewChild('notificationForm', { static: false })
  sendNotificationForm: SendNotificationFormComponent;

  constructor(
    protected readonly automationService: AutomationViewService,
    protected readonly usersService: UsersService,
    protected readonly fb: UntypedFormBuilder,
    private injector: Injector,
  ) {
    super();
    this.automationService
      .isNonPartnerAutomation()
      .pipe(
        take(1),
        map((isNonPartnerAutomation) => {
          if (isNonPartnerAutomation) {
            this.usersFormControl.disable();
            return true;
          }
          this.usersFormControl.setValidators(Validators.required);
          return false;
        }),
      )
      .subscribe();
  }

  ngOnInit(): void {
    super.ngOnInit();
    this.sscLinkTemplate = '{{sales_url}}/info/{{business_id}}';
    const jsonData = this.step ? JSON.parse(this.step.data) : {};
    this.usersFormControl.setValue(jsonData.user_ids || [ASSIGNED_SALESPERSON]);
    this.jobTitleFormControl.setValue(jsonData.job_title);
    this.form = this.fb.group({
      userIds: this.usersFormControl,
    });

    // When the user list is loaded, saving is possible.
    this.usersFormControl.valueChanges
      .pipe(
        map(
          (selectedSalespeopleIDs) =>
            selectedSalespeopleIDs.filter((s) => s !== ASSIGNED_SALESPERSON && s !== ADDITIONAL_SALESPEOPLE).length > 0,
        ),
      )
      .subscribe((individualUserIDsSelected) => {
        individualUserIDsSelected ? this.disableJobTitleControl() : this.enableJobTitleControl();
      });
  }

  getRules(): RuleInterface[] {
    return [];
  }

  getData(): any {
    const jsonData = this.step ? JSON.parse(this.step.data) : {};

    const notification = this.sendNotificationForm.getData();

    jsonData.notification_id = GenericNotificationID;
    jsonData.notification_channel = notification?.notificationChannel;
    jsonData.user_ids = this.usersFormControl.value;
    jsonData.job_title = this.jobTitleFormControl.value;
    jsonData.email_notification = {
      link_template: notification?.emailNotification?.linkTemplate,
      button_text_template: notification?.emailNotification?.buttonTextTemplate,
      subject_template: notification?.emailNotification?.subjectTemplate,
      text_template: notification?.emailNotification?.textTemplate,
    };
    jsonData.in_app_notification = {
      link_template: notification?.inAppNotification?.linkTemplate,
      text_template: notification?.inAppNotification?.textTemplate,
    };

    return jsonData;
  }

  getControlsToValidateOnSave(): (UntypedFormControl | UntypedFormGroup | UntypedFormArray)[] {
    return [this.form, this.sendNotificationForm.form];
  }

  isFormValid(): boolean {
    return !this.form.invalid && this.sendNotificationForm.isFormValid();
  }
  private disableJobTitleControl(): void {
    this.jobTitleFormControl.disable();
    this.jobTitleFormControl.setValue(null);
  }

  private enableJobTitleControl(): void {
    this.jobTitleFormControl.enable();
  }

  finishedInitialLoading(): void {
    this.savingEnabled = true;
  }
}
